import { styled } from '@mui/material';
import { TextAreaStyleType } from './TextArea';

export const TextAreaSectionStyled = styled('section', {
  shouldForwardProp: (prop) => !['inline'].includes(prop as string),
})(({ theme, inline }) => ({
  width: '100%',
  display: 'flex',
  flex: '1',
  flexDirection: 'column',
  [theme.breakpointsMediaQuery.up('md')]: {
    paddingRight: theme.fonts.utility.size(1),
  },
  [theme.breakpointsMediaQuery.down('md')]: {
    ...(inline && {
      paddingRight: theme.fonts.utility.size(1),
    }),
  },
}));

export const TextAreaContainer = styled('div')(() => ({
  position: 'relative',
  display: 'flex',
}));

export const TextAreaStyled = styled('textarea', {
  shouldForwardProp: (prop) =>
    !['styleType', 'withIcon'].includes(prop as string),
})(({ theme, styleType, withIcon }) => {
  const defaultStyle = {
    color: theme.colors.primaryText,
    backgroundColor: theme.colors.white,
    cursor: 'initial',
  };

  const readOnlyStyle = {
    color: theme.colors.primaryText,
    backgroundColor: theme.colors.readOnly,
    cursor: 'initial',
  };

  const disabledStyle = {
    color: theme.colors.primaryText,
    backgroundColor: theme.colors.disabled,
    cursor: 'not-allowed',
  };

  const contactFormModalStyle = {
    color: theme.colors.primaryText,
    backgroundColor: theme.colors.white,
    cursor: 'initial',
    borderTop: '2px solid ' + theme.colors.borderInput,
    borderBottom: '2px solid ' + theme.colors.borderInput,
    borderLeft: '2px solid ' + theme.colors.borderInput,
    borderRight: '2px solid ' + theme.colors.borderInput,
    resize: 'none',
  };

  const contactFormPageStyle = {
    color: theme.colors.black,
    backgroundColor: theme.colors.white,
    cursor: 'initial',
    border: '1px solid ' + theme.colors.black,
    resize: 'none',
    height: '400px',
  };

  const getStyle = (): any => {
    switch (styleType) {
      case TextAreaStyleType.default:
      case TextAreaStyleType.workshop:
      default:
        return defaultStyle;
      case TextAreaStyleType.readOnly:
        return readOnlyStyle;
      case TextAreaStyleType.disabled:
        return disabledStyle;
      case TextAreaStyleType.contactFormModal:
        return contactFormModalStyle;
      case TextAreaStyleType.contactFormPage:
        return contactFormPageStyle;
    }
  };

  return {
    '&:focus': {
      outline: 'none',
    },

    WebkitAppearance: 'none',

    resize: 'vertical',
    fontFamily: theme.fonts.default.regular.fontFamily,

    transition: '0s',

    flex: '1',

    fontSize: '0.9em',

    borderRadius: '2px',

    padding: `${theme.spacings.unit(1.25)} ${theme.spacings.unit(2)}`,

    border: (() => {
      let result = `2px solid ${theme.colors.borderInput}`;
      result = withIcon ? `2px solid ${theme.colors.border1}` : result;
      result =
        styleType === TextAreaStyleType.readOnly
          ? `2px solid ${theme.colors.border1}`
          : result;
      result =
        styleType === TextAreaStyleType.disabled
          ? `2px solid ${theme.colors.disabled}`
          : result;
      return result;
    })(),

    ...getStyle(),

    '&:before': {
      ...(withIcon && {
        background: 'blue',
        position: 'absolute',
        top: theme.spacings.unit(0),
        left: theme.spacings.unit(0),
        backgroundColor: theme.colors.inputIconBackground,
      }),
    },

    '::placeholder': {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: theme.colors.inputPlaceholder,
      fontFamily: theme.fonts.default.regular.fontFamily,
      opacity: '1' /* Firefox */,
      fontSize: '0.9em',
    },

    ':-ms-input-placeholder': {
      /* Internet Explorer 10-11 */
      color: theme.colors.inputPlaceholder,
      fontFamily: theme.fonts.default.regular.fontFamily,
      fontSize: '0.9em',
    },

    '::-ms-input-placeholder': {
      /* Microsoft Edge */
      color: theme.colors.inputPlaceholder,
      fontFamily: theme.fonts.default.regular.fontFamily,
      fontSize: '0.9em',
    },

    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
      {
        transition: 'background-color 5000s',
        '-webkit-text-fill-color': theme.colors.onSurface,
      },
  };
});

export const TextAreaLabelStyled = styled('label', {
  shouldForwardProp: (prop) =>
    !['styleType', 'withIcon'].includes(prop as string),
})(({ theme, styleType }) => {
  const defaultStyle = {
    color: theme.colors.primaryText,
  };

  const contactFormModalStyle = {
    color: theme.colors.primaryText,
  };

  const getStyle = (): any => {
    switch (styleType) {
      case TextAreaStyleType.default:
      default:
        return defaultStyle;
      case TextAreaStyleType.contactFormModal:
        return contactFormModalStyle;
    }
  };

  return {
    ...getStyle(),
    minHeight: theme.spacings.unit(1.5),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.fonts.utility.size(8),
    margin: `${theme.spacings.unit(1)} 0`,
    fontWeight: '600',
    svg: {
      path: {
        fill: theme.colors.primaryText,
      },
      marginRight: '8px',
    },
  };
});

export const ErrorStyled = styled('label')(({ theme }) => ({
  fontSize: theme.fonts.utility.size(5),
  margin: `${theme.spacings.unit(0.25)} 0`,
  '&&': {
    color: theme.colors.primaryError,
  },
  minHeight: theme.spacings.unit(1),
}));
